import React, { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useProfileCompletion } from '../hooks/useProfileCompletion'
import './ProfileCompletionGuide.css'

const WHY_MESSAGE =
  'Used to personalize your letters (cover letters, outreach emails). We don\'t share this with anyone.'

export const ProfileCompletionGuide: React.FC = () => {
  const { profile } = useAuth()
  const { steps, completedCount, totalCount, score, isComplete } = useProfileCompletion(profile)
  const [isExpanded, setIsExpanded] = useState(false)

  const handleCollapse = useCallback(() => {
    setIsExpanded(false)
  }, [])

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  const handleStepNavigate = useCallback(() => {
    setIsExpanded(false)
  }, [])

  if (!profile || isComplete) {
    return null
  }

  return (
    <div
      className={`profile-completion-guide ${isExpanded ? 'expanded' : 'collapsed'}`}
      role="region"
      aria-label="Profile completion"
    >
      <button
        type="button"
        className="profile-completion-guide-handle"
        onClick={handleToggle}
        aria-expanded={isExpanded}
        aria-label={isExpanded ? 'Collapse profile completion' : 'Expand profile completion'}
      >
        <span className="profile-completion-guide-handle-icon" aria-hidden>
          ✓
        </span>
        <span className="profile-completion-guide-handle-text">Profile</span>
        <span className="profile-completion-guide-handle-progress">
          {score}%
        </span>
      </button>

      <div className="profile-completion-guide-panel" aria-hidden={!isExpanded}>
        <div className="profile-completion-guide-header">
          <h3 className="profile-completion-guide-title">Complete your profile</h3>
          <div className="profile-completion-guide-header-actions">
            <button
              type="button"
              className="profile-completion-guide-collapse"
              onClick={handleCollapse}
              aria-label="Collapse"
            >
              ⟨
            </button>
          </div>
        </div>
        <p className="profile-completion-guide-why">{WHY_MESSAGE}</p>
        <div className="profile-completion-guide-progress">
          <div className="profile-completion-guide-progress-text">
            <span>
              {completedCount} of {totalCount} complete
            </span>
            <span className="profile-completion-guide-progress-score">{score}%</span>
          </div>
          <div
            className="profile-completion-guide-progress-bar"
            style={{ ['--profile-completion-pct' as string]: `${score}%` }}
          >
            <div
              className="profile-completion-guide-progress-fill"
              role="progressbar"
              aria-label="Profile completion progress"
              aria-valuenow={score}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        </div>
        <div
          className="profile-completion-guide-timeline"
          style={{ ['--profile-completion-pct' as string]: `${score}%` }}
        >
          <span className="profile-completion-guide-timeline-line" aria-hidden />
          {steps.map((step, index) => (
            <Link
              key={step.id}
              to={step.linkTo}
              className={`profile-completion-guide-step ${step.completed ? 'completed' : 'pending'}`}
              onClick={handleStepNavigate}
            >
              <span className="profile-completion-guide-step-dot" aria-hidden>
                {step.completed ? '✓' : index + 1}
              </span>
              <span className="profile-completion-guide-step-body">
                <span className="profile-completion-guide-step-label">{step.label}</span>
                <span className="profile-completion-guide-step-meta">
                  {step.completed ? 'Completed' : 'Missing'}
                </span>
              </span>
              <span className="profile-completion-guide-step-action">
                {step.completed ? 'Edit' : 'Add'}
              </span>
            </Link>
          ))}
        </div>
        <div className="profile-completion-guide-footer">
          <Link to="/profile" className="profile-completion-guide-cta" onClick={handleStepNavigate}>
            Go to Profile
          </Link>
        </div>
      </div>
    </div>
  )
}
